import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import { fmtBkk } from '../../utils/bkkTime';

// The "Scheduled" folder under the sidebar's More row (Bugify "Schedule Send Emails", Calem P0):
// the user's own emails waiting to go out — send time (tenant zone), To, Subject — each with Cancel,
// which puts the email back in Drafts. A failed send stays here with its reason until cancelled.
type Row = { id: string; accountEmail: string; to: string[]; cc: string[]; subject: string; attachments: { name: string }[]; sendAt: number; status: string; error: string };

export default function ScheduledList() {
  const { t } = useTranslation();
  const { uiLang, showToast, loadEmails, activeFolder } = useStore();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState('');
  const load = () => api.scheduledEmails().then((d: any) => setRows(d.rows || [])).catch(() => setRows([]));
  useEffect(() => { load(); const h = setInterval(load, 30_000); return () => clearInterval(h); }, [activeFolder]);   // eslint-disable-line

  const cancel = async (r: Row) => {
    if (busy || !window.confirm(t('eml_sched_cancel_confirm'))) return;
    setBusy(r.id);
    try { await api.cancelScheduled(r.id); showToast(t('eml_sched_cancelled')); await load(); loadEmails({ silent: true }); }
    catch (e: any) { showToast(e?.message || 'Failed'); }
    finally { setBusy(''); }
  };
  const pill = (r: Row) => {
    const m = r.status === 'failed' ? { bg: '#FEE2E2', c: '#B91C1C', k: 'eml_sched_st_failed' } : r.status === 'sending' ? { bg: '#FEF9C3', c: '#A16207', k: 'eml_sched_st_sending' } : { bg: '#EFF6FF', c: '#1D4ED8', k: 'eml_sched_st_queued' };
    return <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: m.bg, color: m.c, whiteSpace: 'nowrap' }}>{t(m.k)}</span>;
  };

  return (
    <div style={{ flex: 1, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'var(--bg-panel)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--border)', minHeight: 52, boxSizing: 'border-box' }}>
        <i className="ti ti-calendar-time" style={{ fontSize: 15, color: 'var(--accent)' }} aria-hidden />
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t('eml_scheduled')}</span>
        {rows && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{rows.length}</span>}
        <div style={{ flex: 1 }} />
        <button onClick={load} title={t('refresh')} style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}><i className="ti ti-refresh" style={{ fontSize: 15 }} aria-hidden /></button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {rows === null ? null : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('eml_sched_empty')}</div>
        ) : rows.map(r => (
          <div key={r.id} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: r.status === 'failed' ? '#B91C1C' : 'var(--accent)', whiteSpace: 'nowrap' }}>
                <i className="ti ti-clock" style={{ fontSize: 12 }} aria-hidden /> {fmtBkk(r.sendAt * 1000, uiLang)}
              </span>
              {pill(r)}
              <div style={{ flex: 1 }} />
              <button disabled={busy === r.id || r.status === 'sending'} onClick={() => cancel(r)} style={{ padding: '4px 10px', fontSize: 11.5, fontWeight: 600, borderRadius: 'var(--radius-md)', border: '1px solid rgba(226,75,74,0.35)', background: 'transparent', color: '#E24B4A', cursor: r.status === 'sending' ? 'not-allowed' : 'pointer', opacity: busy === r.id || r.status === 'sending' ? 0.5 : 1 }}>
                {t('eml_sched_cancel')}
              </button>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}><b style={{ color: 'var(--text-primary)' }}>{t('to')}:</b> {[...r.to, ...r.cc].join(', ') || '—'}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.subject || '(No subject)'}
              {r.attachments.length > 0 && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-tertiary)' }}><i className="ti ti-paperclip" aria-hidden /> {r.attachments.length}</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.accountEmail}{r.error ? <span style={{ color: '#B91C1C' }}> · {r.error}</span> : null}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
